import { Link } from "react-router-dom";
import { Phone, MapPin, ChevronRight, HardHat, Home, Building2, Recycle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import FAQ from "@/components/FAQ";

const faqData = [
  {
    question: "What's included in demolition service?",
    answer: "Complete demolition includes structural teardown, debris removal, site cleanup, and proper disposal. We also coordinate permits, utility disconnections, and environmental compliance as needed."
  },
  {
    question: "How long does demolition take?",
    answer: "A single-family home demo typically takes 1-3 days. Larger commercial buildings may take 1-2 weeks. Selective interior demo is often completed in 1-2 days depending on scope."
  },
  {
    question: "Do you salvage reusable materials?",
    answer: "Yes, we can salvage and separate materials for donation or resale including fixtures, lumber, brick, and metal. This reduces waste and may offset some demolition costs."
  },
  {
    question: "What permits are needed for demolition?",
    answer: "Most municipalities require a demolition permit. We handle all permit applications and ensure compliance with local regulations, environmental requirements, and safety standards."
  }
];

const demolitionServices = [
  {
    title: "Residential Demolition",
    description: "Safe teardown of houses, garages, and outbuildings with complete debris removal.",
    icon: Home,
    features: ["House demolition", "Garage teardown", "Shed removal", "Foundation removal"]
  },
  {
    title: "Commercial Demolition",
    description: "Strategic demolition of commercial buildings, warehouses, and industrial structures.",
    icon: Building2,
    features: ["Building teardown", "Warehouse demo", "Retail spaces", "Office buildings"]
  },
  {
    title: "Selective Demolition",
    description: "Precise interior demo for renovations without disturbing surrounding structures.",
    icon: HardHat,
    features: ["Interior gut-outs", "Wall removal", "Fixture removal", "Asbestos abatement"]
  },
  {
    title: "Debris & Hauling",
    description: "Responsible disposal and recycling of demolition debris.",
    icon: Recycle,
    features: ["Material sorting", "Recycling", "Dump fees included", "Clean site turnover"]
  }
];

const serviceAreas = [
  { name: "Niagara Region", link: "/locations" },
  { name: "Hamilton", link: "/locations/hamilton" },
  { name: "Toronto & GTA", link: "/locations/toronto" },
  { name: "Mississauga", link: "/locations/mississauga" }
];

export default function DemolitionServices() {
  return (
    <>
      <SEOMeta
        title="Demolition Contractor — Residential & Commercial | Niagara • Hamilton • GTA"
        description="Safe, efficient demolition for homes and buildings in Niagara, Hamilton & the GTA. Selective demo, hauling and clean site turnover."
        keywords="demolition contractor Niagara, Hamilton demolition services, GTA demolition, selective demolition, site clearing, debris hauling, disposal"
        canonicalUrl="https://blueknightcontracting.ca/services/demolition"
      />
      <LocalBusinessSchema
        locationName="Demolition Services"
        addressLocality="Niagara Falls"
        addressRegion="ON"
        description="Professional demolition services for residential and commercial properties"
        services={["Residential Demolition", "Commercial Demolition", "Selective Demo", "Debris Removal"]}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Demolition Services
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Safe, efficient demolition across Niagara, Hamilton & the GTA
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">
                  Get Free Quote <ChevronRight className="h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="gap-2">
                <a href="tel:+12899370797">
                  <Phone className="h-5 w-5" /> (289) 937-0797
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Demolition Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From complete teardowns to selective interior demo, we handle projects of all sizes safely and efficiently
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {demolitionServices.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-primary" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why Choose Blue Knight?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Safety & Compliance</CardTitle>
                  <CardDescription>
                    Fully licensed and insured with strict safety protocols and environmental compliance
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Efficient Execution</CardTitle>
                  <CardDescription>
                    Modern equipment and experienced crews ensure fast, clean demolition with minimal disruption
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Responsible Disposal</CardTitle>
                  <CardDescription>
                    We recycle and properly dispose of all materials, meeting all environmental regulations
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Service Areas</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceAreas.map((area, index) => (
                <Link key={index} to={area.link}>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <MapPin className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{area.name}</CardTitle>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQ faqs={faqData} />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="bg-gradient-to-br from-primary/10 to-accent/10">
              <CardHeader>
                <CardTitle className="text-3xl mb-4">Need Demolition Services?</CardTitle>
                <CardDescription className="text-lg mb-6">
                  Contact us for a free site visit and demolition quote
                </CardDescription>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a href="tel:+12899370797">(289) 937-0797</a>
                  </Button>
                </div>
              </CardHeader>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
